import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import authService from '../../services/authService';
import siteService from '../../services/siteService';
import { Search, Plus, Edit3, ChevronDown, ChevronUp, Users, UserMinus, UserPlus, Filter, Loader } from 'lucide-react';

const AllSites = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    const fetchSites = async () => {
      setLoading(true);
      try {
        const response = await siteService.getAllSites();
        console.log('Sites response:', response);
        if (response && Array.isArray(response.sites)) {
          setSites(response.sites);
        } else {
          throw new Error('Received unexpected data format from the server.');
        }
      } catch (error) {
        console.error('Error fetching sites:', error);
        setError(error.message || 'An error occurred while fetching the sites.');
      } finally {
        setLoading(false);
      }
    };

    fetchSites();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedSites = useMemo(() => {
    let sortableSites = [...sites];
    if (sortConfig.key) {
      sortableSites.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableSites;
  }, [sites, sortConfig]);

  const filteredSites = useMemo(() => {
    return sortedSites.filter(site =>
      site.site_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (site.study_name && site.study_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [sortedSites, searchTerm]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSites.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader className="w-16 h-16 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 m-4" role="alert">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">All Sites</h1>
        
        <div className="mb-6 flex justify-between items-center">
          <div className="relative">
            <input
              type="text"
              placeholder="Search sites..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          </div>
          <div className="flex space-x-4">
            <button className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
              <Filter size={20} className="mr-2" />
              Filter
            </button>
            <Link to="/add-site" className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
              <Plus size={20} className="mr-2" />
              Add New Site
            </Link>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 dark:shadow-gray-900/20 shadow overflow-hidden sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Site Name', 'Study', 'Institution', 'Country', 'Active', 'Deactivated', 'Total', 'Actions'].map((header) => (
                    <th
                      key={header}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer whitespace-nowrap"
                      onClick={() => requestSort(header.toLowerCase().replace(' ', '_'))}
                    >
                      <div className="flex items-center">
                        {header}
                        {sortConfig.key === header.toLowerCase().replace(' ', '_') && (
                          sortConfig.direction === 'ascending' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                {currentItems.map((site) => (
                  <tr key={site.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/studies/${site.study_external_id}/sites/${site.external_id}/`} className="text-primary-600 hover:text-primary-900">
                        {site.site_name}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{site.study_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{site.institution}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{site.country}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserPlus size={14} className="text-green-600 mr-2" />
                        <span className="text-gray-900">{site.active_participants_count}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserMinus size={14} className="text-red-600 mr-2" />
                        <span className="text-gray-900">{site.deactivated_participants_count}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users size={14} className="text-primary-600 mr-2" />
                        <span className="text-gray-900">{site.total_participants_count}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link to={`/studies/${site.study_external_id}/sites/${site.external_id}/edit`} className="text-primary-600 hover:text-primary-900">
                        <Edit3 size={18} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredSites.length)}</span> of{' '}
              <span className="font-medium">{filteredSites.length}</span> results
            </p>
          </div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm dark:shadow-gray-900/20 -space-x-px" aria-label="Pagination">
            {Array.from({ length: Math.ceil(filteredSites.length / itemsPerPage) }).map((_, index) => (
              <button
                key={index}
                onClick={() => paginate(index + 1)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  currentPage === index + 1
                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default AllSites;